import { action, observable, runInAction } from "mobx";
import { BaseEditViewStore } from "core/stores";
import { NikoTemplateCreateViewStore } from "application/niko-patient/stores";
import { NikoPatientTemplateService } from "application/niko-patient/services";
import { LookupService } from "common/services";
import _ from 'lodash';
import { PatientTemplateCreateForm } from "../forms";

class NikoCreateTemplateModalStore extends BaseEditViewStore {
  @observable accessor patientTemplateItems = [];
  @observable accessor patientTemplateTrachItems = [];
  @observable accessor disabled = false;
  constructor(rootStore, componentProps) {
    const nikoTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
    const nikoPatientDetail =  componentProps.modalParams.data.nikoPatientDetail;
    const pageName = componentProps.modalParams.data.pageName;
    const tabName = componentProps.modalParams.data.tabName;

    super(rootStore, {
      name: "niko-template-create-modal",
      Form: PatientTemplateCreateForm,
      actions: () => {
        return {
          create: async (items, resource) => {
            let patientTemplateItems = items.filter((x) => x.product);
            patientTemplateItems.forEach((i) => delete i.id);
            let request = {
              ...resource,
              nikoPatientDetailId: nikoPatientDetail.id,
              facilityOrderId: nikoPatientDetail.facilityOrderId,
              pageName: pageName,
              tabName: tabName,
              patientTemplateItems: patientTemplateItems.map(i =>
              ({
                productId: i.productId,
                quantity: i.quantity,
                salesOrderQuantity: i.salesOrderQuantity,
                note: i.note,
                procedureCode: i.procedureCode,
                mlph: i.mlph,
                frequency: i.frequency,
                itemCals: i.itemCals,
                itemMls: i.itemMls
              })),
            };

            const response = await nikoTemplateService.create(request);
            return response.data;
          },
        };
      },
      FormClass: PatientTemplateCreateForm,
    });

    this.rootStore = rootStore;
    this.modalParams = componentProps.modalParams;
    this.nikoPatientDetail = nikoPatientDetail;
    this.nikoTemplateService = nikoTemplateService;
    this.fetchPatientTemplates = this.modalParams.data.fetchPatientTemplates;
    this.pageName = pageName;
    this.tabName = tabName;

    this.rootStore.viewStore.setNavigationOptions({
      title: "",
    });

    this.nikoTemplateCreateViewStore = new NikoTemplateCreateViewStore(this.rootStore, {
      patientTemplateItems: this.patientTemplateItems,
      patientTemplateTrachItems: this.patientTemplateTrachItems,
      form: this.form,
      facilityId: this.nikoPatientDetail.facilityId,
      facilityOrderId: this.nikoPatientDetail.facilityOrderId,
    });

    this.fetch([this.initialize()]);
  }

  @action
  async initialize() {
    await this.getLookups();
    this.form.clear();
  }

  @action.bound
  async getLookups() {
    const lookupService = new LookupService(this.rootStore.application.baasic.apiClient);
    const response = await lookupService.getLookups("ItemCategory,SubCategory");
    runInAction(() => {
      this.itemCategories = response.itemCategory;
      this.subCategories = response.subCategory;
    });
  }

  @action.bound
  async createResource(resource) {
    if (!this.actions.create) return;
    this.disabled = true;
    this.form.setFieldsDisabled(true);

    try {
      if (this.translationStore) {
        this.translationStore.applyMetadata(resource);
      }

      if (this.patientTemplateTrachItems.some(i => i.product)) {
        const respiratorySubCategory = this.subCategories.find(x => x.abrv == 'Respiratory---trachs-supply');
        const trachPatientTemplate = {
          ...resource,
          facilityOrderId: this.nikoPatientDetail.facilityOrderId,
          subCategoryId: respiratorySubCategory.id,
          patientTemplateItems: this.patientTemplateTrachItems.filter(i => i.product),
          pageName: this.pageName,
          tabName: this.tabName
        };
        _.extend(resource, { trachTemplate: trachPatientTemplate })
      }

      const created = await this.actions.create(this.patientTemplateItems, resource);

      // ISG-871: run drainage bag / leg bag validations for a newly created template only when BOTH the patient is
      // Active AND the template was automatically USI-reviewed on creation.
      let validationMessages = [];
      if (created && created.id && created.isUsIntakeReviewed && this.nikoPatientDetail && this.nikoPatientDetail.status === 'Active') {
        const validationResponse = await this.nikoTemplateService.drainageBagValidation([created.id]);
        validationMessages = (validationResponse && validationResponse.data && validationResponse.data.messages) || [];
      }

      if (this.fetchPatientTemplates) {
        await this.fetchPatientTemplates();
      }

      this.disabled = false;
      this.form.setFieldsDisabled(false);
      this.modalParams.close();

      this.rootStore.notificationStore.success("Template successfully created.");
      validationMessages.forEach((message) => this.rootStore.notificationStore.warning(message));
    } catch (err) {
      this.disabled = false;
      this.form.setFieldsDisabled(false);
      this.rootStore.notificationStore.error(err && err.data ? err.data : "Error occured while trying to create new facility order template.");
    }
  }
}

export default NikoCreateTemplateModalStore;
